import { type FC, useContext, useId, useLayoutEffect } from 'react';
import { default as useMeasure } from 'react-use-measure';
import { NumberKit } from '@team/number';
import {
  type PrimitivePropsWithRef,
  Primitive as primitive,
} from '@radix-ui/react-primitive';
import { type SafeAreaInsets } from '../types';
import { Context } from '../context';

type Props = WithChildren<{
  position: keyof SafeAreaInsets;
}> &
  Pick<PrimitivePropsWithRef<typeof primitive.div>, 'asChild'>;

export const RegisterContentInset: FC<Props> = ({
  position,
  children,
  asChild,
}) => {
  const id = useId();
  const [ref, bounds] = useMeasure();
  const { registerContentSafeAreaInsets, unregisterContentSafeAreaInsets } =
    useContext(Context);

  /** Устанавливает вертикальный отступ */
  useLayoutEffect(() => {
    if (!NumberKit.isPositive(bounds.height)) {
      return;
    }

    if (!['top', 'bottom'].includes(position)) {
      return;
    }

    registerContentSafeAreaInsets(id, { [position]: bounds.height });

    return () => {
      unregisterContentSafeAreaInsets(id);
    };
  }, [
    id,
    position,
    bounds.height,
    registerContentSafeAreaInsets,
    unregisterContentSafeAreaInsets,
  ]);

  /** Устанавливает горизонтальный отступ */
  useLayoutEffect(() => {
    if (!NumberKit.isPositive(bounds.width)) {
      return;
    }

    if (!['left', 'right'].includes(position)) {
      return;
    }

    registerContentSafeAreaInsets(id, { [position]: bounds.width });

    return () => {
      unregisterContentSafeAreaInsets(id);
    };
  }, [
    id,
    position,
    bounds.width,
    registerContentSafeAreaInsets,
    unregisterContentSafeAreaInsets,
  ]);

  return (
    <primitive.div ref={ref} asChild={asChild}>
      {children}
    </primitive.div>
  );
};
